import React, { useEffect, useState } from 'react';
import type { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import { subscribeToPersistor } from '../../store/features/storeReference';

/**
 * Wrapper component that waits for persistor to be created before rendering children
 *
 * This ensures scoped persistence is set up before rehydration begins.
 * The persistor is created lazily after user authentication completes and
 * initializeScopedPersistence() is called.
 *
 * Uses a subscription pattern instead of polling to avoid CPU waste.
 */
export const DelayedPersistGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [persistor, setPersistor] = useState<Persistor | null>(null);

  useEffect(() => {
    // Subscribe to persistor creation
    // If persistor already exists, callback fires immediately
    const unsubscribe = subscribeToPersistor(newPersistor => {
      setPersistor(newPersistor);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // If no persistor yet, render children without persistence (unauthenticated state)
  if (!persistor) {
    return <>{children}</>;
  }

  // Once persistor is available, use PersistGate normally
  return (
    <PersistGate loading={null} persistor={persistor}>
      {children}
    </PersistGate>
  );
};
